import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FeedbackQuickModal from '@/components/FeedbackQuickModal';
import { feedbackAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Clock3, Loader2, MessageCircle, ShieldCheck, Pencil, Ban, Trash2 } from 'lucide-react';
import { applyCleanOrLegacyParam, buildHandleMaps, resolveHandleOrLegacyId } from '@/lib/queryHandles';

const MODULE_OPTIONS = ['ALL', 'POS', 'SERVICES', 'INVENTORY', 'PRINT', 'TEMPLATES', 'PURCHASES', 'GAMING', 'AR', 'SETTINGS', 'OTHER'];
const STATUS_OPTIONS = ['ALL', 'new', 'in_review', 'resolved', 'cancelled'];
const SEVERITY_OPTIONS = ['ALL', 'low', 'medium', 'high', 'critical'];

const statusBadgeClass = {
  new: 'bg-sky-100 text-sky-700 border-sky-300',
  in_review: 'bg-amber-100 text-amber-700 border-amber-300',
  resolved: 'bg-green-100 text-green-700 border-green-300',
  cancelled: 'bg-slate-200 text-slate-700 border-slate-400',
};

const severityBadgeClass = {
  low: 'bg-slate-100 text-slate-700 border-slate-300',
  medium: 'bg-blue-100 text-blue-700 border-blue-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  critical: 'bg-red-100 text-red-700 border-red-300',
};

const formatDateTimeMaputo = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-MZ', {
    timeZone: 'Africa/Maputo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(dt).replace(',', '');
};

export default function FeedbackPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ new_count: 0, in_review_count: 0, resolved_count: 0, cancelled_count: 0, total_open: 0 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [commentMessage, setCommentMessage] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({ module: 'OTHER', severity: 'low', title: '', description: '', location_id: '' });

  const [filters, setFilters] = useState({
    status: 'ALL',
    module: 'ALL',
    severity: 'ALL',
    q: '',
  });

  const canResolve = useMemo(() => {
    const role = String(user?.role || '');
    return ['admin', 'store_manager', 'supervisor'].includes(role);
  }, [user?.role]);

  const canAdminManage = useMemo(() => String(user?.role || '') === 'admin', [user?.role]);
  const ticketHandleMaps = useMemo(() => buildHandleMaps(tickets, (ticket) => ticket.title || ticket.id), [tickets]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await feedbackAPI.getStats();
      setStats(response.data || {});
    } catch {
      setStats({ new_count: 0, in_review_count: 0, resolved_count: 0, cancelled_count: 0, total_open: 0 });
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 100,
      };
      if (filters.status !== 'ALL') params.status = filters.status;
      if (filters.module !== 'ALL') params.module = filters.module;
      if (filters.severity !== 'ALL') params.severity = filters.severity;
      if (filters.q.trim()) params.q = filters.q.trim();

      const response = await feedbackAPI.listTickets(params);
      const rows = response?.data?.items || [];
      setTickets(rows);

      const ticketIdFromQuery = resolveHandleOrLegacyId({
        cleanValue: searchParams.get('ticket'),
        legacyValue: searchParams.get('ticket_id'),
        handleToId: buildHandleMaps(rows, (ticket) => ticket.title || ticket.id).handleToId,
      });
      if (ticketIdFromQuery) {
        const found = rows.find(t => t.id === ticketIdFromQuery);
        if (found) setSelectedTicket(found);
      }

      if (!selectedTicket && rows.length > 0) {
        setSelectedTicket(rows[0]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  }, [filters, searchParams, selectedTicket]);

  const fetchTicketDetail = useCallback(async (ticketId) => {
    if (!ticketId) return;
    try {
      const response = await feedbackAPI.getTicket(ticketId);
      setSelectedTicket(response.data);
    } catch {
      toast.error('Erro ao carregar detalhe do ticket');
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets, fetchStats]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('ticket');
    params.delete('ticket_id');
    applyCleanOrLegacyParam({
      params,
      cleanKey: 'ticket',
      legacyKey: 'ticket_id',
      value: selectedTicket?.id,
      idToHandle: ticketHandleMaps.idToHandle,
    });
    if (params.toString() === searchParams.toString()) return;
    setSearchParams(params, { replace: true });
  }, [searchParams, selectedTicket?.id, setSearchParams, ticketHandleMaps.idToHandle]);

  const markInReview = async () => {
    if (!selectedTicket) return;
    try {
      setUpdatingStatus(true);
      const response = await feedbackAPI.updateStatus(selectedTicket.id, { status: 'in_review' });
      setSelectedTicket(response.data);
      toast.success('Ticket marcado Em Análise');
      await Promise.all([fetchTickets(), fetchStats()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao actualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const markResolved = async () => {
    if (!selectedTicket) return;
    if (!canResolve) {
      toast.error('Sem permissão para resolver ticket');
      return;
    }
    if (!statusNote.trim()) {
      toast.error('Nota é obrigatória para resolver');
      return;
    }

    try {
      setUpdatingStatus(true);
      const response = await feedbackAPI.updateStatus(selectedTicket.id, {
        status: 'resolved',
        note: statusNote.trim(),
      });
      setSelectedTicket(response.data);
      setStatusNote('');
      toast.success('Ticket resolvido');
      await Promise.all([fetchTickets(), fetchStats()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao resolver ticket');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const addComment = async () => {
    if (!selectedTicket || !commentMessage.trim()) return;
    try {
      setAddingComment(true);
      const response = await feedbackAPI.addComment(selectedTicket.id, { message: commentMessage.trim() });
      setSelectedTicket(response.data);
      setCommentMessage('');
      toast.success('Comentário adicionado');
      await fetchTickets();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao comentar');
    } finally {
      setAddingComment(false);
    }
  };

  const openEditDialog = () => {
    if (!selectedTicket) return;
    setEditForm({
      module: selectedTicket.module || 'OTHER',
      severity: selectedTicket.severity || 'low',
      title: selectedTicket.title || '',
      description: selectedTicket.description || '',
      location_id: selectedTicket.location_id || '',
    });
    setEditDialogOpen(true);
  };

  const saveTicketEdit = async () => {
    if (!selectedTicket) return;
    try {
      setSavingEdit(true);
      const response = await feedbackAPI.updateTicket(selectedTicket.id, editForm);
      setSelectedTicket(response.data);
      setEditDialogOpen(false);
      toast.success('Ticket actualizado pelo administrador');
      await Promise.all([fetchTickets(), fetchStats()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao editar ticket');
    } finally {
      setSavingEdit(false);
    }
  };

  const cancelTicket = async () => {
    if (!selectedTicket) return;
    try {
      setUpdatingStatus(true);
      const response = await feedbackAPI.cancelTicket(selectedTicket.id, { note: statusNote.trim() || 'Ticket cancelado pelo administrador' });
      setSelectedTicket(response.data);
      setStatusNote('');
      toast.success('Ticket cancelado');
      await Promise.all([fetchTickets(), fetchStats()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao cancelar ticket');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const deleteTicket = async () => {
    if (!selectedTicket) return;
    try {
      setUpdatingStatus(true);
      await feedbackAPI.deleteTicket(selectedTicket.id);
      toast.success('Ticket apagado pelo administrador');
      setSelectedTicket(null);
      setStatusNote('');
      await Promise.all([fetchTickets(), fetchStats()]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao apagar ticket');
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="feedback-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Feedback In-App</h1>
          <p className="text-sm text-muted-foreground">Tickets internos do staff (Maputo timezone)</p>
        </div>
        <FeedbackQuickModal onCreated={(ticket) => {
          setSelectedTicket(ticket);
          fetchTickets();
          fetchStats();
        }} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="feedback-stats-cards">
        <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Novos</p><p className="text-2xl font-bold" data-testid="feedback-stats-new">{stats.new_count || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Em análise</p><p className="text-2xl font-bold" data-testid="feedback-stats-review">{stats.in_review_count || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Resolvidos</p><p className="text-2xl font-bold" data-testid="feedback-stats-resolved">{stats.resolved_count || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Cancelados</p><p className="text-2xl font-bold" data-testid="feedback-stats-cancelled">{stats.cancelled_count || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Abertos</p><p className="text-2xl font-bold" data-testid="feedback-stats-open">{stats.total_open || 0}</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2" data-testid="feedback-list-card">
          <CardHeader>
            <CardTitle>Lista de Tickets</CardTitle>
            <CardDescription>Filtrar por estado, módulo, severidade e pesquisa</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="feedback-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Módulo</Label>
                <Select value={filters.module} onValueChange={(v) => setFilters((p) => ({ ...p, module: v }))}>
                  <SelectTrigger data-testid="feedback-filter-module"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODULE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severidade</Label>
                <Select value={filters.severity} onValueChange={(v) => setFilters((p) => ({ ...p, severity: v }))}>
                  <SelectTrigger data-testid="feedback-filter-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITY_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pesquisa</Label>
                <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Nº, título, descrição" data-testid="feedback-filter-search" />
              </div>
            </div>

            <div className="border rounded-md overflow-auto" data-testid="feedback-tickets-table-wrapper">
              <table className="w-full min-w-[980px] text-sm" data-testid="feedback-tickets-table">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Nº</th>
                    <th className="px-3 py-2 text-left">Data</th>
                    <th className="px-3 py-2 text-left">Módulo</th>
                    <th className="px-3 py-2 text-left">Severidade</th>
                    <th className="px-3 py-2 text-left">Título</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2 text-left">Criado por</th>
                    <th className="px-3 py-2 text-left">Localização</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className="px-3 py-4" colSpan={8}>A carregar...</td></tr>
                  ) : tickets.length === 0 ? (
                    <tr><td className="px-3 py-4 text-muted-foreground" colSpan={8}>Sem tickets</td></tr>
                  ) : tickets.map(ticket => (
                    <tr key={ticket.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => fetchTicketDetail(ticket.id)} data-testid={`feedback-ticket-row-${ticket.id}`}>
                      <td className="px-3 py-2 font-mono" data-testid={`feedback-ticket-number-${ticket.id}`}>{ticket.ticket_number}</td>
                      <td className="px-3 py-2" data-testid={`feedback-ticket-created-at-${ticket.id}`}>{formatDateTimeMaputo(ticket.created_at)}</td>
                      <td className="px-3 py-2" data-testid={`feedback-ticket-module-${ticket.id}`}>{ticket.module}</td>
                      <td className="px-3 py-2" data-testid={`feedback-ticket-severity-${ticket.id}`}>
                        <Badge className={severityBadgeClass[ticket.severity] || ''} variant="outline">{ticket.severity}</Badge>
                      </td>
                      <td className="px-3 py-2" data-testid={`feedback-ticket-title-${ticket.id}`}>{ticket.title}</td>
                      <td className="px-3 py-2" data-testid={`feedback-ticket-status-${ticket.id}`}>
                        <Badge className={statusBadgeClass[ticket.status] || ''} variant="outline">{ticket.status}</Badge>
                      </td>
                      <td className="px-3 py-2" data-testid={`feedback-ticket-created-by-${ticket.id}`}>{ticket.created_by_user_name}</td>
                      <td className="px-3 py-2" data-testid={`feedback-ticket-location-${ticket.id}`}>{ticket.location_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="feedback-detail-card">
          <CardHeader>
            <CardTitle>Detalhe do Ticket</CardTitle>
            <CardDescription>Histórico e resolução</CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedTicket ? (
              <p className="text-muted-foreground text-sm">Selecione um ticket na lista</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold" data-testid="feedback-detail-ticket-number">{selectedTicket.ticket_number}</h3>
                    <Badge className={statusBadgeClass[selectedTicket.status] || ''} variant="outline" data-testid="feedback-detail-status-badge">{selectedTicket.status}</Badge>
                  </div>
                  <p className="text-sm mt-1" data-testid="feedback-detail-title">{selectedTicket.title}</p>
                  <p className="text-xs text-muted-foreground" data-testid="feedback-detail-created-at">{formatDateTimeMaputo(selectedTicket.created_at)}</p>
                </div>

                <div className="text-sm space-y-1">
                  <p data-testid="feedback-detail-module"><strong>Módulo:</strong> {selectedTicket.module}</p>
                  <p data-testid="feedback-detail-severity"><strong>Severidade:</strong> {selectedTicket.severity}</p>
                  <p data-testid="feedback-detail-description"><strong>Descrição:</strong> {selectedTicket.description}</p>
                  <p data-testid="feedback-detail-location"><strong>Localização:</strong> {selectedTicket.location_name || '-'}</p>
                  <p data-testid="feedback-detail-notification-status"><strong>Email when enabled:</strong> {selectedTicket.notification_status || 'skipped_smtp'}</p>
                </div>

                {selectedTicket.screenshot?.data_base64 && (
                  <div className="space-y-2" data-testid="feedback-detail-screenshot-section">
                    <p className="text-xs font-medium">Screenshot</p>
                    <img
                      src={`data:${selectedTicket.screenshot.content_type};base64,${selectedTicket.screenshot.data_base64}`}
                      alt="feedback-screenshot"
                      className="w-full max-h-52 object-contain border rounded"
                      data-testid="feedback-detail-screenshot-preview"
                    />
                    <p className="text-xs text-muted-foreground" data-testid="feedback-detail-screenshot-meta">
                      {selectedTicket.screenshot.filename} • {Math.round((selectedTicket.screenshot.size_bytes || 0) / 1024)} KB • {selectedTicket.screenshot.content_type}
                    </p>
                  </div>
                )}

                <div className="space-y-2 border-t pt-3">
                  <p className="text-sm font-medium flex items-center gap-2"><Clock3 className="h-4 w-4" />Workflow</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" disabled={updatingStatus || selectedTicket.status !== 'new'} onClick={markInReview} data-testid="feedback-mark-in-review-button">
                      {updatingStatus ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Marcar Em Análise
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={updatingStatus || selectedTicket.status === 'resolved' || selectedTicket.status === 'cancelled' || !canResolve}
                      onClick={markResolved}
                      data-testid="feedback-mark-resolved-button"
                    >
                      {updatingStatus ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                      Marcar Resolvido
                    </Button>
                    {canAdminManage && (
                      <>
                        <Button size="sm" variant="outline" onClick={openEditDialog} data-testid="feedback-admin-edit-button">
                          <Pencil className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button size="sm" variant="outline" disabled={updatingStatus || selectedTicket.status === 'cancelled'} onClick={cancelTicket} data-testid="feedback-admin-cancel-button">
                          <Ban className="h-4 w-4 mr-2" />
                          Cancelar
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive" disabled={updatingStatus} onClick={deleteTicket} data-testid="feedback-admin-delete-button">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Apagar
                        </Button>
                      </>
                    )}
                  </div>
                  {!canResolve && (
                    <p className="text-xs text-amber-700" data-testid="feedback-resolve-role-warning">
                      Sem permissão de role para resolver (admin/store_manager/supervisor)
                    </p>
                  )}
                  <Textarea
                    rows={3}
                    placeholder={canAdminManage ? 'Nota de resolução/cancelamento' : 'Nota de resolução (obrigatória para resolver)'}
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    data-testid="feedback-resolve-note-input"
                  />
                </div>

                <div className="space-y-2 border-t pt-3">
                  <p className="text-sm font-medium">Comentários</p>
                  <div className="max-h-40 overflow-auto space-y-2" data-testid="feedback-comments-list">
                    {(selectedTicket.comments || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">Sem comentários</p>
                    ) : (selectedTicket.comments || []).map(c => (
                      <div key={c.id} className="border rounded p-2 text-xs" data-testid={`feedback-comment-${c.id}`}>
                        <p className="font-medium">{c.user_name}</p>
                        <p>{c.message}</p>
                        <p className="text-muted-foreground">{formatDateTimeMaputo(c.at)}</p>
                      </div>
                    ))}
                  </div>
                  <Textarea rows={2} value={commentMessage} onChange={(e) => setCommentMessage(e.target.value)} placeholder="Adicionar comentário" data-testid="feedback-comment-input" />
                  <Button size="sm" variant="outline" onClick={addComment} disabled={addingComment || !commentMessage.trim()} data-testid="feedback-add-comment-button">
                    {addingComment ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Adicionar Comentário
                  </Button>
                </div>

                <div className="space-y-2 border-t pt-3">
                  <p className="text-sm font-medium">Timeline / Audit</p>
                  <div className="max-h-44 overflow-auto space-y-2" data-testid="feedback-audit-log-list">
                    {(selectedTicket.audit_log || []).map((entry, idx) => (
                      <div key={`${entry.at}-${idx}`} className="text-xs border rounded p-2" data-testid={`feedback-audit-log-${idx}`}>
                        <p className="font-medium">{entry.action}</p>
                        <p>{entry.from_status || '-'} → {entry.to_status || '-'}</p>
                        {entry.note ? <p>{entry.note}</p> : null}
                        <p className="text-muted-foreground">{entry.user_name} • {formatDateTimeMaputo(entry.at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="feedback-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar Ticket</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Módulo</Label>
              <Select value={editForm.module} onValueChange={(value) => setEditForm((prev) => ({ ...prev, module: value }))}>
                <SelectTrigger data-testid="feedback-edit-module"><SelectValue /></SelectTrigger>
                <SelectContent>{MODULE_OPTIONS.filter((option) => option !== 'ALL').map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severidade</Label>
              <Select value={editForm.severity} onValueChange={(value) => setEditForm((prev) => ({ ...prev, severity: value }))}>
                <SelectTrigger data-testid="feedback-edit-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITY_OPTIONS.filter((option) => option !== 'ALL').map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Título</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))} data-testid="feedback-edit-title" />
            </div>
            <div className="md:col-span-2">
              <Label>Descrição</Label>
              <Textarea rows={5} value={editForm.description} onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))} data-testid="feedback-edit-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="feedback-edit-cancel-dialog">Fechar</Button>
            <Button onClick={saveTicketEdit} disabled={savingEdit} data-testid="feedback-edit-save-button">
              {savingEdit ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
